import { Reducer } from 'redux';
import { createAction, ActionTypes } from 'client/core/store/actions';
import { useSelector, shallowEqual } from 'react-redux';
import { RootState } from 'client/core/store/RootReducer';
import { IUser } from 'common/schema/User';
import { IClientConfig } from 'common/config/IClientConfig';
import { fromPairs } from 'lodash';
import { RolesLogic } from 'common/logic/RolesLogic';
import { IClientModuleConfig } from 'common/config/IModulesConfig';
import { useCurrentUser } from '../auth/AuthModule';

export const ClientConfigActions = {
  loaded: createAction('@clientConfig/loaded', (config: IClientConfig) => ({
    config
  }))
};

export interface ClientConfigState {
  config: IClientConfig | undefined;
  transformedModules: {
    [key: string]: IClientModuleConfig;
  };
  transformedTheme: {
    options: { [key: string]: any };
    labels: { [key: string]: string };
  };
}

export const ClientConfigReducer: Reducer<
  ClientConfigState,
  ActionTypes<typeof ClientConfigActions>
> = (
  state = {
    config: undefined,
    transformedModules: {},
    transformedTheme: {
      options: {},
      labels: {}
    }
  },
  action
) => {
  switch (action.type) {
    /**
     * Al caricamento, salviamo la configurazione
     */
    case '@clientConfig/loaded':
      return {
        ...state,
        config: action.payload.config,
        transformedModules: fromPairs(
          action.payload.config.modules.map(m => [m.feature, m])
        ),
        transformedTheme: {
          options: fromPairs(
            action.payload.config.theme.options.map(o => [o.name, o.value])
          ),
          labels: fromPairs(
            action.payload.config.theme.labels.map(l => [l.name, l.text])
          )
        }
      };

    default:
      return state;
  }
};

/**
 * Hook per ottenere la configurazione corrente
 */
export const useClientConfig = () =>
  useSelector((state: RootState) => state.config.config, shallowEqual);

/**
 * Hook per ottenere le opzioni di configurazione per l'UI (tema)
 */
export const useThemeConfig = () =>
  useSelector(
    (state: RootState) => state.config.transformedTheme,
    shallowEqual
  );

/**
 * Hook per ottenere le opzioni di configurazione per dei moduli
 */
export const useModulesConfig = () =>
  useSelector(
    (state: RootState) => state.config.transformedModules,
    shallowEqual
  );

export const useModuleConfigOption = (feature: string, option: string) =>
  useSelector(
    (state: RootState) =>
      state.config.transformedModules[feature]?.options?.find(
        o => o.name === option
      ),
    shallowEqual
  );

/**
 * Hook per ottenere i ruoli disponibili
 */
export const useAvailableRoleOptions = () => {
  const user = useCurrentUser();
  const config = useClientConfig();

  return RolesLogic.availableRolesOptions(
    user,
    config?.modules.map(m => m.feature)
  );
};
